import { useState, useEffect } from 'react'
import { X, ArrowRightLeft, Check, AlertCircle, Minus, Search } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

// Parse CF/Mux video lists from various API response shapes
function parseVideoList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data.result)) return data.result
  if (data.result?.videos && Array.isArray(data.result.videos)) return data.result.videos
  if (data.videos && Array.isArray(data.videos)) return data.videos
  if (data.data && Array.isArray(data.data)) return data.data
  return []
}

// Find the best title match in a library of assets
function findBestMatch(searchTitle, assets, getTitleFn) {
  if (!searchTitle) return null
  const search = searchTitle.toLowerCase().substring(0, 20)
  const matches = assets.filter(a => getTitleFn(a).toLowerCase().includes(search))
  if (matches.length === 0) return null
  if (matches.length === 1) return matches[0]
  // Multiple matches — pick shortest title (most specific match)
  return matches.sort((a, b) => getTitleFn(a).length - getTitleFn(b).length)[0]
}

export default function BulkTypeSwitchModal({ items, onClose, onComplete, setStatus }) {
  const { api } = useAuth()

  // Step 1: Target type selection
  const [targetType, setTargetType] = useState('')

  // Step 2: Library loading + matching
  const [loading, setLoading] = useState(false)
  const [muxAssets, setMuxAssets] = useState([])
  const [cfStreams, setCfStreams] = useState([])
  const [matchResults, setMatchResults] = useState([]) // { tbtvId, title, status, matchedTitle, newMediaUrl, newDuration }

  // Step 3: Apply
  const [processing, setProcessing] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] })

  // When target type changes, load library and run matching
  useEffect(() => {
    if (!targetType) {
      setMatchResults([])
      return
    }
    runMatching()
  }, [targetType])

  async function runMatching() {
    setLoading(true)
    setMatchResults([])

    try {
      let results = []

      if (targetType === 'MUX') {
        // Load Mux assets
        let assets = muxAssets
        if (!assets.length) {
          const data = await api('/mux/assets?limit=100')
          assets = data.data || data || []
          setMuxAssets(assets)
        }

        results = items.map(item => {
          if (item.video_type === 'MUX') {
            return { tbtvId: item.tbtv_id, title: item.title, status: 'skipped', matchedTitle: null, newMediaUrl: null, newDuration: null }
          }
          const match = findBestMatch(item.title, assets, a => a.meta?.title || a.passthrough || a.id)
          if (match) {
            const pid = match.playback_ids?.[0]?.id
            return {
              tbtvId: item.tbtv_id,
              title: item.title,
              status: 'matched',
              matchedTitle: match.meta?.title || match.passthrough || match.id,
              newMediaUrl: pid ? `https://stream.mux.com/${pid}.m3u8` : null,
              newDuration: match.duration ? Math.floor(match.duration) : null
            }
          }
          return { tbtvId: item.tbtv_id, title: item.title, status: 'no_match', matchedTitle: null, newMediaUrl: null, newDuration: null }
        })

      } else if (targetType === 'CF') {
        // Load CF Streams
        let streams = cfStreams
        if (!streams.length) {
          const data = await api('/videos?per_page=100')
          streams = parseVideoList(data)
          setCfStreams(streams)
        }

        results = items.map(item => {
          if (item.video_type === 'CF') {
            return { tbtvId: item.tbtv_id, title: item.title, status: 'skipped', matchedTitle: null, newMediaUrl: null, newDuration: null }
          }
          const match = findBestMatch(item.title, streams, s => s.meta?.name || s.uid)
          if (match) {
            const cfHls = match.playback?.hls ||
              (match.uid ? `https://customer-bs9kri9klnfqt57u.cloudflarestream.com/${match.uid}/manifest/video.m3u8` : null)
            return {
              tbtvId: item.tbtv_id,
              title: item.title,
              status: cfHls ? 'matched' : 'no_match',
              matchedTitle: match.meta?.name || match.uid,
              newMediaUrl: cfHls,
              newDuration: match.duration ? Math.floor(match.duration) : null
            }
          }
          return { tbtvId: item.tbtv_id, title: item.title, status: 'no_match', matchedTitle: null, newMediaUrl: null, newDuration: null }
        })

      } else if (targetType === 'YT') {
        // YouTube — check if items have youtube_id
        results = items.map(item => {
          if (item.video_type === 'YT') {
            return { tbtvId: item.tbtv_id, title: item.title, status: 'skipped', matchedTitle: null, newMediaUrl: null, newDuration: null }
          }
          const ytId = item.youtube_id
          if (ytId) {
            return {
              tbtvId: item.tbtv_id,
              title: item.title,
              status: 'matched',
              matchedTitle: `YouTube: ${ytId}`,
              newMediaUrl: `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`,
              newDuration: null
            }
          }
          return { tbtvId: item.tbtv_id, title: item.title, status: 'no_match', matchedTitle: 'No YouTube link', newMediaUrl: null, newDuration: null }
        })

      } else {
        // MP4 or HLS — no library to match, just switch the type (keep existing URL)
        results = items.map(item => {
          if (item.video_type === targetType) {
            return { tbtvId: item.tbtv_id, title: item.title, status: 'skipped', matchedTitle: null, newMediaUrl: null, newDuration: null }
          }
          return {
            tbtvId: item.tbtv_id,
            title: item.title,
            status: 'matched',
            matchedTitle: `Keep existing URL`,
            newMediaUrl: item.media_url,
            newDuration: null
          }
        })
      }

      setMatchResults(results)
    } catch (e) {
      setStatus(`Error loading library: ${e.message}`)
    }
    setLoading(false)
  }

  async function handleApply() {
    const toApply = matchResults.filter(r => r.status === 'matched' && r.newMediaUrl)
    if (toApply.length === 0) return

    setProcessing(true)
    setProgress({ done: 0, total: toApply.length, errors: [] })

    for (let i = 0; i < toApply.length; i++) {
      const item = toApply[i]
      try {
        const patch = { video_type: targetType, media_url: item.newMediaUrl }
        if (item.newDuration) patch.duration = item.newDuration
        await api(`/vod/${item.tbtvId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch)
        })
      } catch (e) {
        setProgress(prev => ({
          ...prev,
          errors: [...prev.errors, { tbtvId: item.tbtvId, error: e.message }]
        }))
      }
      setProgress(prev => ({ ...prev, done: i + 1 }))
    }

    setProcessing(false)
    const skipped = matchResults.filter(r => r.status !== 'matched').length
    const errorCount = progress.errors.length
    const successCount = toApply.length - errorCount
    setStatus(`✅ Switched ${successCount} item(s) to ${targetType}${skipped ? ` (${skipped} skipped)` : ''}${errorCount ? ` (${errorCount} errors)` : ''}`)
    onComplete()
  }

  const matchedCount = matchResults.filter(r => r.status === 'matched' && r.newMediaUrl).length
  const noMatchCount = matchResults.filter(r => r.status === 'no_match').length
  const skippedCount = matchResults.filter(r => r.status === 'skipped').length

  return (
    <div className="media-picker-overlay" onClick={onClose}>
      <div className="media-picker-modal bulk-type-switch-modal" onClick={e => e.stopPropagation()}>
        <div className="media-picker-modal-header">
          <h2><ArrowRightLeft size={16} /> Bulk Switch Type — {items.length} item{items.length !== 1 ? 's' : ''}</h2>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        {/* Step 1: Select target type */}
        <div className="bulk-type-switch-step">
          <label>Switch to:</label>
          <div className="bulk-type-switch-options">
            {['MUX', 'CF', 'YT', 'MP4', 'HLS'].map(t => (
              <button
                key={t}
                className={`bulk-type-btn ${targetType === t ? 'active' : ''}`}
                onClick={() => setTargetType(t)}
                disabled={processing}
              >
                {t === 'YT' ? 'YouTube' : t}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bulk-type-switch-loading">
            <Search size={14} className="spinning" /> Searching {targetType} library for matches...
          </div>
        )}

        {/* Step 2: Match results */}
        {matchResults.length > 0 && !loading && (
          <>
            <div className="bulk-type-switch-summary">
              <span className="match-stat matched"><Check size={12} /> {matchedCount} matched</span>
              {noMatchCount > 0 && <span className="match-stat no-match"><AlertCircle size={12} /> {noMatchCount} no match</span>}
              {skippedCount > 0 && <span className="match-stat skipped"><Minus size={12} /> {skippedCount} already {targetType}</span>}
            </div>

            <div className="bulk-type-switch-results">
              {matchResults.map(r => (
                <div key={r.tbtvId} className={`bulk-type-row ${r.status}`}>
                  <div className="bulk-type-row-icon">
                    {r.status === 'matched' && <Check size={14} />}
                    {r.status === 'no_match' && <AlertCircle size={14} />}
                    {r.status === 'skipped' && <Minus size={14} />}
                  </div>
                  <div className="bulk-type-row-info">
                    <div className="bulk-type-row-title">{r.title}</div>
                    {r.status === 'matched' && (
                      <div className="bulk-type-row-match">→ {r.matchedTitle}</div>
                    )}
                    {r.status === 'no_match' && (
                      <div className="bulk-type-row-nomatch">{r.matchedTitle || 'No match found'}</div>
                    )}
                    {r.status === 'skipped' && (
                      <div className="bulk-type-row-skip">Already {targetType}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Progress bar during apply */}
        {processing && (
          <div className="bulk-type-switch-progress">
            <div className="bulk-tag-progress-bar">
              <div
                className="bulk-tag-progress-fill"
                style={{ width: `${(progress.done / progress.total) * 100}%` }}
              />
            </div>
            <span>{progress.done} / {progress.total}</span>
            {progress.errors.length > 0 && (
              <span className="bulk-tag-errors">{progress.errors.length} errors</span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="bulk-type-switch-footer">
          <button className="btn-ghost" onClick={onClose} disabled={processing}>Cancel</button>
          <button
            className="btn-primary"
            onClick={handleApply}
            disabled={!targetType || matchedCount === 0 || processing || loading}
          >
            {processing
              ? `Switching ${progress.done}/${progress.total}...`
              : `Switch ${matchedCount} item${matchedCount !== 1 ? 's' : ''} to ${targetType || '...'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
